/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */
import React, { useContext, useState, useEffect } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ThemeContext } from "../../contexts/ThemeContext";
import ErrorBoundary from "../general/ErrorBoundary";
import GeneralNavbar from "../general/GeneralNavbar";
import PsychometricService from "../../services/PsychometricService";
import PsychometricTestNavbar from '../psychometric/PyschometricTestNavbar';
const PsychometricTestPersonalityLikert = ({ 
  goToNextStep, 
  goToPreviousStep, 
  history, 
  location,
  isOrganization,
  signedID,
  signedFirstName,
  signedLastName,
  signedOrganization,
  signedEmail,
  ...props 
}) => {
  const { theme, fontFamily, colors, fontWeight } = useContext(ThemeContext);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const isIframe = new URLSearchParams(location.search).get('iframe') === 'true';
  const likertOptions = [
    "Strongly Disagree",
    "Disagree",
    "Neutral",
    "Agree",
    "Strongly Agree",
  ];

  useEffect(() => {


    // Validate required parameters based on user type
    if (isOrganization === true) {
      if (!signedOrganization || !signedFirstName || !signedLastName || !signedID) {
        console.log("Redirecting due to missing organization parameters...");
        history.replace("/psychometricSignIn");
        return;
      }
    } else {
      if (!signedEmail) {
        console.log("Redirecting due to missing email...");
        history.replace("/psychometricSignIn");
        return;
      }
    }

    const fetchQuestions = async () => {
      try {
        const response = await PsychometricService.getPsychometricTest("english");
        const section = response.test.sections.find((section) => section.number === "2");
        setQuestions(Object.values(section.properties));
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading questions:", error);
      }
    };

    fetchQuestions();
  }, [isOrganization, signedID, signedFirstName, signedLastName, signedOrganization, signedEmail, history]);

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionIndex]: value,
    }));
  };

  const updateAnswer = async (questionIndex, value) => {
    const answerValue = likertOptions.indexOf(value) + 1;
    try {
      if (isOrganization) {
        await PsychometricService.updateTraitsAnswers(
          signedID,
          signedFirstName,
          signedLastName,
          signedOrganization,
          questionIndex,
          answerValue
        );
      } else {
        // Add method for individual users
        await PsychometricService.updateIndividualTraitsAnswers(
          signedEmail,
          questionIndex,
          answerValue
        );
      }
    } catch (error) {
      console.error("Error updating answer:", error.response ? error.response.data : error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!answers[currentQuestionIndex]) {
      setModalOpen(true);
      return;
    }
    await updateAnswer(currentQuestionIndex, answers[currentQuestionIndex]);
    goToNextStep();
  };

  const handleNextQuestion = async () => {
    if (!answers[currentQuestionIndex]) {
      setModalOpen(true);
      return;
    }
    await updateAnswer(currentQuestionIndex, answers[currentQuestionIndex]);
    setCurrentQuestionIndex((prevIndex) => Math.min(prevIndex + 1, questions.length - 1));
  };

  const handlePreviousQuestion = async () => {
    if (answers[currentQuestionIndex]) {
      await updateAnswer(currentQuestionIndex, answers[currentQuestionIndex]);
    }
    setCurrentQuestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const handleNextPage = async () => {
    if (currentQuestionIndex !== questions.length - 1) {
      return;
    }
    await updateAnswer(currentQuestionIndex, answers[currentQuestionIndex]);
    
    // Construct next path based on user type
    let nextPath;
    if (isOrganization) {
      nextPath = `/psychometricTestInterestsIntro/${encodeURIComponent(signedID)}/${encodeURIComponent(signedFirstName)}/${encodeURIComponent(signedLastName)}/${encodeURIComponent(signedOrganization)}`;
    } else {
      nextPath = `/psychometricTestInterestsIntro/${encodeURIComponent(signedEmail)}`;
    }
    
    const fullPath = isIframe ? `${nextPath}?iframe=true` : nextPath;
    history.push(fullPath);
  };

  // Calculate the progress percentage
  const maxProgressPercentage = 20;
  const totalQuestions = questions.length;
  const progressPercentage = Math.min(
    ((currentQuestionIndex + 1) / Math.min(totalQuestions, 10)) * maxProgressPercentage,
    maxProgressPercentage
  );

  return (
    <div style={{ fontFamily: fontFamily.primary, color: colors.text }}>
      {isIframe && (
        <ErrorBoundary errorAt="At PsychometricTestPersonalityLikert PsychometricTestNavbar">
          <PsychometricTestNavbar signedOrganization={signedOrganization} />
        </ErrorBoundary>
      )}
      {!isIframe && (
        <ErrorBoundary errorAt="At General PsychometricTestPersonalityLikert GeneralNavbar">
          <PsychometricTestNavbar signedOrganization={signedOrganization} />
        </ErrorBoundary>
      )}
      
      <div className="progress" style={{ height: "10px", marginBottom: "10px" }}>
        <div
          className="progress-bar bg-success"
          role="progressbar"
          style={{
            width: `${progressPercentage}%`,
            background: "linear-gradient(to right, #28a745 20%, transparent 20%)",
          }}
          aria-valuenow={progressPercentage}
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
      <div className="row p-3 px-5">
        <div
          className="col-md-12 col-12 mb-4"
          style={{
            textAlign: "center",
            fontSize: "1.5rem",
            fontWeight: fontWeight.bold,
            color: colors.primaryGreenDark,
          }}
        >
          Psychometric Test: Your Personality
        </div>
        {isLoading ? (
          <div className="col-md-12 col-12 mb-4" style={{ textAlign: "center" }}>
            Loading...
          </div>
        ) : (
          <div className="col-md-12 col-12 mb-4" style={{ textAlign: "left" }}>
            <div className="mb-4" style={{ fontSize: "1.2rem" }}>
              <p>
                <strong>
                  {currentQuestionIndex + 1} / {questions.length}
                </strong>
                :
                <span className="ml-1" id='question'>
                  {questions[currentQuestionIndex]}
                </span> 
              </p>
              <div
                className="likert-options"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  marginLeft: "10px",
                }}
              >
                {likertOptions.map((option, index) => (
                  <label
                    key={index}
                    style={{
                      margin: "5px 0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestionIndex}`}
                      value={option}
                      checked={answers[currentQuestionIndex] === option}
                      onChange={() => handleAnswerChange(currentQuestionIndex, option)}
                    />
                    <span style={{ marginLeft: "5px", fontWeight: "normal" }}>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <Button 
                onClick={handlePreviousQuestion}  
                style={{
                  ...theme.newButtonRounded,
                  opacity: currentQuestionIndex === 0 ? 0.5 : 1
                }} 
                color="secondary"
                disabled={currentQuestionIndex === 0}
              >
                Back
              </Button>
              <Button 
                onClick={handleNextQuestion}  
                style={{
                  ...theme.newButtonRounded,
                  opacity: currentQuestionIndex === questions.length - 1 ? 0.5 : 1
                }} 
                color="primary"
                disabled={currentQuestionIndex === questions.length - 1}
              >
                Next
              </Button>
            </div>

            {currentQuestionIndex === questions.length - 1 && (
              <div className="mt-4 d-flex justify-content-between">
                <Button
                  type="button"
                  color="primary"
                  style={theme.formSubmit}
                  onClick={handleSubmit}
                >
                  Next Page
                </Button>
              </div>
            )}
          </div>
        )}
            <footer style={theme.psychometricTestFooter}>
                Powered by Umang Wellness
            </footer>
      </div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>Incomplete Answer</ModalHeader>
        <ModalBody>Please select an answer before proceeding to the next question.</ModalBody>
        <ModalFooter>
          <Button color="primary" id="modalclose" style={theme.formSubmit} onClick={() => setModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PsychometricTestPersonalityLikert;
